import React from "react";
import { Modal, Button } from "@weezevent/nacre";
import { FormProvider, useForm } from "react-hook-form";
import {
	FormFieldSelect,
	FormFieldCheckbox,
	FormFieldRadioLabel
} from "components/form-helpers";
import { Trans, useTranslation } from "react-i18next";
import Style from "./duplicate-checklists.module.scss";
import PropTypes from "prop-types";
import useEventList from "api/api-hooks/useEventList";
import useOrganizationContext from "hooks/useOrganizationContext";
import { SelectChecklists } from "./components";
import { ActionsTypes } from "../../constants";
import { useEventConfig } from "api/api-hooks/event-config/use-get-event-config";

const DEFAULT_VALUES = {
	eventId: null,
	periodId: null,
	should_archive_source_checklists: true,
	actionType: ActionsTypes.TRANSFER_CHECKLISTS
};

const mapToSelectOptions = (data, valueKey = "id", textKey = "name") =>
	data?.map(item => ({
		value: item[valueKey],
		text: item[textKey]
	})) || [];

export const DuplicateChecklists = ({
	onSubmit,
	onCancel,
	isSubmitting,
	checklistsIds
}) => {
	const { t } = useTranslation();
	const organization = useOrganizationContext();
	const organizationId = organization?.id();

	const form = useForm({ defaultValues: DEFAULT_VALUES });
	const { control, watch, getValues, setValue } = form;

	const shouldSetRestArchived = watch("should_archive_source_checklists");
	const selectedActionType = watch("actionType");
	const eventId = watch("eventId");

	const checklistPeriods = checklistsIds.map(id => watch(`periodId_${id}`));

	// Check if a period is missing
	const hasMissingPeriods = checklistPeriods.some(period => !period);

	const { data: events } = useEventList({
		params: {
			with_config: true
		}
	});

	const { data: eventConfig } = useEventConfig({
		eventId,
		options: { enabled: Boolean(eventId) }
	});

	const secondaryEvents = eventConfig?.events || [];
	const mainEventId = eventConfig?.id || null;

	const eventOptions = React.useMemo(() => mapToSelectOptions(events), [
		events
	]);

	const handleCheckboxChange = checked => {
		setValue("should_archive_source_checklists", checked);
	};

	const handleSubmit = () => {
		const {
			eventId,
			actionType,
			should_archive_source_checklists
		} = getValues();

		const config = checklistsIds
			.filter(checklistId => {
				const checklist = getValues()[`checklist_${checklistId}`];
				return !checklist?.isDisabled;
			})
			.map(checklistId => {
				const periodId = getValues()[`periodId_${checklistId}`];
				return {
					checkpoint_id: checklistId,
					period_id: periodId,
					event_id: eventId,
					should_duplicate_checklist:
						actionType === ActionsTypes.DUPLICATE_CHECKLISTS,
					should_archive_source_checklist:
						actionType === ActionsTypes.DUPLICATE_CHECKLISTS
							? should_archive_source_checklists
							: false
				};
			});

		onSubmit({
			eventId: eventId || null,
			actionType,
			config
		});
	};

	const placeholderText = t("formFieldMultiSelectEventPlaceholder", {
		count: eventOptions?.length
	});

	return (
		<>
			<Modal
				onClose={onCancel}
				title={
					<Trans i18nKey="access.checkpoint.batchChecklists.duplicateChecklists.modal.title" />
				}
				size="extralarge"
				cancelButton={
					<Button disabled={isSubmitting} onClick={onCancel}>
						{t("access.common.cancel")}
					</Button>
				}
				submitButton={
					<Button
						disabled={isSubmitting || !eventId || hasMissingPeriods}
						onClick={handleSubmit}
						primary
					>
						<Trans i18nKey="access.checkpoint.batchChecklists.duplicateChecklists.modal.applyCta" />
					</Button>
				}
			>
				<div>
					<FormProvider {...form}>
						<p>
							{t(
								"access.checkpoint.batchChecklists.duplicateChecklists.modal.movementTypeTitle"
							)}
						</p>
						<div>
							<FormFieldRadioLabel
								control={control}
								name="actionType"
								value={ActionsTypes.TRANSFER_CHECKLISTS}
								label={t(
									"access.checkpoint.batchChecklists.duplicateChecklists.modal.transferChecklistsLabel"
								)}
								checked={
									selectedActionType ===
									ActionsTypes.TRANSFER_CHECKLISTS
								}
								className={Style["radioLabel"]}
							/>
							<FormFieldRadioLabel
								control={control}
								name="actionType"
								value={ActionsTypes.DUPLICATE_CHECKLISTS}
								label={t(
									"access.checkpoint.batchChecklists.duplicateChecklists.modal.duplicateChecklistsLabel"
								)}
								checked={
									selectedActionType ===
									ActionsTypes.DUPLICATE_CHECKLISTS
								}
								className={Style["radioLabel"]}
							/>
						</div>
						<FormFieldSelect
							control={control}
							name="eventId"
							label={t(
								"access.checkpoint.batchChecklists.duplicateChecklists.modal.eventAccessTitle"
							)}
							placeholder={placeholderText}
							options={eventOptions}
							disabled={eventOptions?.length === 0}
						/>
						{eventId && (
							<>
								<p>
									{t(
										"access.checkpoint.batchChecklists.duplicateChecklists.modal.definePeriodLabel"
									)}
								</p>
								{checklistsIds?.length > 0 && (
									<SelectChecklists
										eventId={eventId}
										organizationId={organizationId}
										checklistsIds={checklistsIds}
										mainEventId={mainEventId}
										secondaryEvents={secondaryEvents}
									/>
								)}
							</>
						)}
						{selectedActionType ===
							ActionsTypes.DUPLICATE_CHECKLISTS && (
							<div className={Style["customModal"]}>
								<FormFieldCheckbox
									control={control}
									name="should_archive_source_checklists"
									label={t(
										"access.checkpoint.batchChecklists.duplicateChecklists.modal.archivecheckboxTitle"
									)}
									checked={shouldSetRestArchived}
									onChange={handleCheckboxChange}
								/>
							</div>
						)}
					</FormProvider>
				</div>
			</Modal>
		</>
	);
};

DuplicateChecklists.propTypes = {
	onSubmit: PropTypes.func,
	onCancel: PropTypes.func,
	isSubmitting: PropTypes.bool,
	checklistsIds: PropTypes.array
};

DuplicateChecklists.defaultProps = {
	onSubmit: () => {},
	onCancel: () => {},
	isSubmitting: false,
	checklistsIds: []
};
